/**
 * GrantScholarDollars — teacher/admin tool to award Scholar Dollars to any student.
 * Grants count as EARNED, so they add to balance and never lower a student's level.
 */
import React, { useEffect, useState } from 'react';
import { Coins, Loader2, Gift, Search, Eye, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const QUICK = [50, 100, 250, 500];

// Human-readable labels for ledger types (falls back to the raw type)
const LEDGER_LABELS = {
  worksheet_perfect: 'Worksheet 100%',
  worksheet_high: 'Worksheet 80–99%',
  combine_set_focus: 'Combine · Target Weakness',
  combine_set_all: 'Combine · All-Around',
  combine_on_fire: 'Combine · On Fire streak',
  teacher_grant: 'Admin Grant',
  lchaim_lesson: "L'Chaim Lesson",
  solo_lesson: 'SOLO Lesson',
  elective_lesson: 'Elective Lesson',
  redeemed: 'Redeemed (spent)',
};
const labelFor = (t) => LEDGER_LABELS[t] || (t || 'unknown').replace(/_/g, ' ');
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; } };

const GrantScholarDollars = () => {
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState('');
  const [amount, setAmount] = useState(100);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/academy/admin/students-lite', { headers: authHeaders() });
      setStudents(r.data.students || []);
    } catch (e) { /* teacher may lack access; keep silent */ }
  };
  useEffect(() => { load(); }, []);

  const filtered = students.filter((s) =>
    !q || (s.name || '').toLowerCase().includes(q.toLowerCase()) || (s.email || '').toLowerCase().includes(q.toLowerCase())
  );
  const selectedStudent = students.find((s) => s.id === selected);

  const grant = async () => {
    if (!selected) { toast.error('Pick a student first'); return; }
    if (!amount) { toast.error('Enter an amount'); return; }
    setBusy(true);
    try {
      const r = await api.post('/academy/admin/grant-dollars',
        { student_id: selected, amount: parseInt(amount, 10), reason },
        { headers: authHeaders() });
      toast.success(`Granted ${r.data.amount} to ${r.data.student.name}. New balance: ${r.data.balance}`);
      setReason('');
      setStudents((prev) => prev.map((s) => s.id === selected ? { ...s, balance: r.data.balance } : s));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not grant Scholar Dollars');
    } finally { setBusy(false); }
  };

  // ── Ledger Audit modal ────────────────────────────────────────────────
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [audit, setAudit] = useState(null);

  const openAudit = async () => {
    if (!selected) { toast.error('Pick a student first'); return; }
    setAuditOpen(true);
    setAuditLoading(true);
    setAudit(null);
    try {
      const r = await api.get(`/academy/admin/student-ledger/${selected}`, { headers: authHeaders() });
      setAudit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load ledger');
      setAuditOpen(false);
    } finally { setAuditLoading(false); }
  };

  return (
    <div className="border-2 border-yellow-500/30 bg-yellow-500/[0.03] p-5" data-testid="grant-dollars-panel">
      <div className="flex items-center gap-2 mb-4">
        <Gift className="w-4 h-4 text-yellow-400" />
        <span className="text-xs font-black tracking-[0.2em] uppercase text-yellow-400">Grant Scholar Dollars</span>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Student</label>
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 text-zinc-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="w-full bg-black border-2 border-zinc-800 pl-8 pr-3 py-2 text-sm text-white outline-none focus:border-yellow-500" data-testid="grant-search" />
          </div>
          <select value={selected} onChange={(e) => setSelected(e.target.value)} className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-yellow-500" data-testid="grant-student-select">
            <option value="">Select a student…</option>
            {filtered.map((s) => (
              <option key={s.id} value={s.id}>{s.name} · G{s.grade_level ?? '?'} · {s.balance ?? 0} SD</option>
            ))}
          </select>
          {selectedStudent && (
            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="text-[11px] text-zinc-400">
                Current balance: <span className="text-yellow-300 font-black">{selectedStudent.balance ?? 0}</span> Scholar Dollars
              </div>
              <button
                onClick={openAudit}
                className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-yellow-300 hover:text-yellow-200 border border-yellow-500/40 hover:border-yellow-400 px-2 py-1"
                data-testid="grant-audit-ledger-btn"
              >
                <Eye className="w-3 h-3" /> Audit Ledger
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Amount</label>
          <div className="flex items-center gap-2 mb-2">
            <Coins className="w-4 h-4 text-yellow-400" />
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-32 bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-yellow-500" data-testid="grant-amount" />
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {QUICK.map((v) => (
              <button key={v} onClick={() => setAmount(v)} className={`px-2.5 py-1 text-[11px] font-black border-2 transition-colors ${parseInt(amount, 10) === v ? 'border-yellow-500 text-yellow-300 bg-yellow-500/10' : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'}`} data-testid={`grant-quick-${v}`}>+{v}</button>
            ))}
          </div>
          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Reason (optional)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Hustle award, perfect attendance" className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-yellow-500" data-testid="grant-reason" />
        </div>
      </div>

      <button onClick={grant} disabled={busy || !selected} className="mt-4 flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black px-5 py-2.5 text-[11px] font-black tracking-[0.2em] uppercase transition-colors" data-testid="grant-submit-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Gift className="w-4 h-4" /> Grant Scholar Dollars</>}
      </button>

      {auditOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setAuditOpen(false)}
          data-testid="audit-ledger-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl max-h-[85vh] overflow-y-auto bg-zinc-950 border-2 border-yellow-500/60 p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.28em] text-yellow-400 mb-1">Scholar Dollar Ledger</div>
                <div className="text-lg font-black text-white">
                  {audit?.student?.name || selectedStudent?.name || '…'}
                </div>
                <div className="text-[11px] text-zinc-500">
                  {audit?.student?.email} · G{audit?.student?.grade_level ?? '?'}
                </div>
              </div>
              <button onClick={() => setAuditOpen(false)} className="text-zinc-400 hover:text-white p-1" data-testid="audit-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {auditLoading && (
              <div className="flex items-center justify-center py-16 text-zinc-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading ledger…
              </div>
            )}

            {!auditLoading && audit && (
              <>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="border-2 border-yellow-500/40 bg-yellow-500/5 p-3">
                    <div className="text-[9px] font-black uppercase tracking-widest text-yellow-400">Balance</div>
                    <div className="text-2xl font-black text-white">{audit.balance?.balance ?? 0}</div>
                  </div>
                  <div className="border-2 border-emerald-500/30 bg-emerald-500/5 p-3">
                    <div className="text-[9px] font-black uppercase tracking-widest text-emerald-400">Total Earned</div>
                    <div className="text-2xl font-black text-white">{audit.totals?.earned ?? 0}</div>
                  </div>
                  <div className="border-2 border-red-500/30 bg-red-500/5 p-3">
                    <div className="text-[9px] font-black uppercase tracking-widest text-red-400">Total Spent</div>
                    <div className="text-2xl font-black text-white">{audit.totals?.spent ?? 0}</div>
                  </div>
                </div>

                <div className="mb-5">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">How they earned it</div>
                  {(audit.breakdown || []).length === 0 ? (
                    <div className="text-xs text-zinc-500 italic">No ledger entries yet.</div>
                  ) : (
                    <div className="border-2 border-zinc-800 divide-y divide-zinc-800">
                      {audit.breakdown.map((b) => (
                        <div key={b.type} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div>
                            <div className="font-bold text-white">{labelFor(b.type)}</div>
                            <div className="text-[10px] text-zinc-500">
                              {b.count}× · first {fmtDate(b.first_at)} · last {fmtDate(b.last_at)}
                            </div>
                          </div>
                          <div className={`font-black tabular-nums ${b.total < 0 ? 'text-red-400' : 'text-yellow-300'}`}>
                            {b.total > 0 ? '+' : ''}{b.total}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">
                    Recent transactions ({audit.entries_shown})
                  </div>
                  <div className="border-2 border-zinc-800 divide-y divide-zinc-800 max-h-80 overflow-y-auto">
                    {(audit.entries || []).map((e, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 px-3 py-2 text-xs">
                        <div className="min-w-0 flex-1">
                          <div className="font-bold text-white">{labelFor(e.type)}</div>
                          <div className="text-[10px] text-zinc-500 truncate">
                            {fmtDate(e.created_at)}
                            {e.reason ? ` · ${e.reason}` : ''}
                            {e.granted_by ? ` · by ${e.granted_by}` : ''}
                          </div>
                        </div>
                        <div className={`font-black tabular-nums flex-shrink-0 ${e.xp < 0 ? 'text-red-400' : 'text-yellow-300'}`}>
                          {e.xp > 0 ? '+' : ''}{e.xp}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GrantScholarDollars;
